/* eslint-disable max-lines-per-function */
/* eslint-disable react/react-in-jsx-scope */
import React, { useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import {
  Button,
  Card,
  CardHeader,
  Collapse,
  Grid,
  IconButton,
} from '@mui/material'
import ErrorFallback from '~/components/ErrorFallback'
import Layout from '~/components/Layout'
import Loading from '~/components/Loading'
import { useLang } from '~/hooks/useLang'
import notify from '~/utils/notify'
import { authFetch } from '~/services/fetch'
import useReportState from '../../../stores/ReportState'
import {
  downloadReportCSV,
} from '../../../services/report'
import { useHookstate } from '@hookstate/core'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FilterAutocompleteDispatchsGroups from '~/components/FilterAutocompleteDispatchsGroups'
import FilterAutocompleteAgencies from '~/components/FilterAutocompleteAgencies'
import FilterSelectMoment from '~/components/FilterAutocompleteMoment'
import GenerateReportButton from '../../../components/GenerateReportButton'
import ExportButtonCSV from '../../../components/ButtonExportCSV'
import FilterAutocompleteDevices from '~/components/FilterAutocompleteDevices'
import FilterAutocompleteDispatchGroupSet from '~/components/FilterAutocompleteDispatchGroupSet'
import { makeRequestToGetReporDevicesByStatus } from './services'
import { generateCSVFileName } from '~/utils/strings'

export default function DeviceByStatus() {
  const { translate } = useLang()
  const isLoading = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const momentDate = useHookstate<string | null>(null)
  const deviceId = useHookstate<number | null>(null)
  const lockFields = useHookstate<boolean>(false)
  const groupId = useHookstate<readonly number[]>([])
  const agencyIds = useHookstate<readonly number[]>([])
  const devicesIds = useHookstate<readonly number[]>([])

  const labelButton = useHookstate('')
  const {
    reportData,
    loadingReport,
    dispatchGroups: { dispatchGroup },
  } = useReportState()
  const groupSetId = useHookstate<number | null>(null)

  useEffect(() => {
    document.title = translate('Reports - Dispatch System')
  }, [])

  useEffect(() => {
    const getDispatchGroups = async () => {
      authFetch({
        url: '/station/dispatch-groups/me',
        method: 'GET',
        data: {},
      }).then((response) => {
        dispatchGroup.options.merge(response.data)
      })
    }

    getDispatchGroups()
  }, [])

  async function generateReport(exportCSV = false) {
    if (momentDate) {
      !exportCSV && loadingReport().set(true)
      isLoading.set(true)
      const data = await makeRequestToGetReporDevicesByStatus({
        momentDate: momentDate.get(),
        agencyIds: agencyIds.get(),
        dispatchGroupsId: groupId.get(),
        deviceIds: devicesIds.get(),
        groupSetId: groupSetId.get(),
        exportCSV: exportCSV
      }).finally(() => {
        loadingReport().set(false)
        isLoading.set(false)
      })

      if (!data || (Array.isArray(data) && data.length === 0)) {
        notify({
          message: translate('Não há dados suficientes para gerar o relatório'),
          type: 'error',
        })
        return
      }


      if (exportCSV) {
        downloadReportCSV(
          generateCSVFileName('List of devices by status on a date', translate),
          data
        )
      } else {
        setTimeout(() => {
          reportData().set(data)
        })
        window.open(`/report/result/devices-by-status`)
      }
    } else {
      notify({
        message: translate('Initial and final date are mandatory'),
        type: 'error',
      })
    }
  }

  const clearFilters = () => {
    agencyIds.set([])
    groupSetId.set(null)
    devicesIds.set([])
    groupId.set([])
    momentDate.set(null)
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout marginLayout={true}>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <Grid container xs={12}>
            <Grid item>
              <CardHeader
                title={`${translate('Report')}: ${translate(
                  `List of devices by status on a date`
                )}`}
              />
            </Grid>
          </Grid>
        </Card>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <CardHeader
            title={`${translate('Filters')}`}
            avatar={
              <IconButton
                onClick={() =>
                  expandFilterSearch.set(!expandFilterSearch.get())
                }
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginRight: 1,
                  padding: 0,
                }}
              >
                {expandFilterSearch.value ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
            }
          />
          <Collapse in={expandFilterSearch.get()}>
            <Grid
              container
              xs={12}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <Grid container direction={'row'} rowGap={2}>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteAgencies
                    disabled={lockFields.get()}
                    agenciesId={agencyIds.get()}
                    onAgenciesChange={(agency) => {
                      labelButton.set(translate('Search'))
                      agencyIds.set(agency)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchGroupSet
                    disabled={lockFields.get()}
                    dispatchGroupSetId={groupSetId?.get() ?? undefined}
                    onGroupDispatchSetChange={(group) => {
                      groupSetId?.set(group)
                    }}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDispatchsGroups
                    disabled={lockFields.get()}
                    agencyId={agencyIds.get() ?? undefined}
                    dispatcheGroups={groupId.get() ?? undefined}
                    onGroupDispatchChange={(group) => {
                      groupId.set(group)
                      deviceId.set(null)
                      labelButton.set(translate('Search'))
                    }}
                    groupSetId={groupSetId.get()}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterAutocompleteDevices
                    devicesIds={devicesIds.get()}
                    onDevicesChange={(device) => {
                      devicesIds.set(device)
                    }}
                    disabled={lockFields.get()}
                    groupDispatchId={groupId.get()}
                  />
                </Grid>
                <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                  <FilterSelectMoment
                    onDateChange={(value) =>
                      momentDate.set(value?.toJSON() ?? null)
                    }
                    disabled={lockFields.get()}
                    date={momentDate.get()}
                    required
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => clearFilters()}
                    sx={{ height: '40px' }}
                  >
                    {translate('Clear Filters')}
                  </Button>
                  <ExportButtonCSV
                    isLoading={isLoading.get()}
                    dataToExport={() => generateReport(true)}
                  />
                  <GenerateReportButton
                    isLoading={isLoading.get()}
                    makeReport={generateReport}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Layout>
    </ErrorBoundary>
  )
}
